/**
 * shared.tsx — panels shared between VerifyPage (/verify/:token) and
 * PublicDossierPage (/v/:token).
 *
 * Extracted from VerifyPage so both pages import consistent components
 * rather than duplicating the render blocks. VerifyPage re-exports these
 * instead of defining them locally.
 *
 * Capacitor readiness:
 *   - No firebase/* imports.
 *   - No navigator.onLine.
 *   - No localStorage/sessionStorage.
 *   - No Web APIs without Capacitor plugin equivalents.
 */

import { Pill } from '@/components/Pill';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import { AlertCircle, Clock, ShieldOff, ShieldQuestion } from 'lucide-react';

// ---------------------------------------------------------------------------
// LoadingPanel
// ---------------------------------------------------------------------------

export function LoadingPanel() {
  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.md,
        background: T.card,
        padding: `${SPACE.md}px ${SPACE.md}px`,
      }}
      role="status"
      aria-label="Loading…"
      aria-live="polite"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
        <ShieldQuestion size={20} color={T.textMuted} aria-hidden="true" />
        <span style={{ fontSize: 15, color: T.textMuted, fontFamily: F.body }}>Loading…</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ExpiredPanel
// ---------------------------------------------------------------------------

export function ExpiredPanel() {
  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderLeft: `3px solid ${T.orange}`,
        borderRadius: RADIUS.md,
        background: T.card,
        padding: `${SPACE.md}px ${SPACE.md}px`,
      }}
      role="status"
      aria-label="Verification result: link expired"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACE.sm,
          marginBottom: SPACE.sm,
        }}
      >
        <Clock size={20} color={T.orange} aria-hidden="true" style={{ flexShrink: 0 }} />
        <h1
          style={{
            fontFamily: F.display,
            fontSize: 20,
            fontWeight: 600,
            color: T.text,
            margin: 0,
          }}
        >
          Link expired
        </h1>
        <Pill color="orange" size="sm">
          Expired
        </Pill>
      </div>
      <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, margin: 0 }}>
        This link has expired. The vehicle owner can sign in to Absolute Pinks and create a fresh
        one.
      </p>
      <p
        style={{
          fontSize: 12,
          color: T.textMuted,
          lineHeight: 1.5,
          marginTop: SPACE.sm,
          marginBottom: 0,
        }}
      >
        The sealed record itself is unchanged. Only the share link timed out.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NotAvailablePanel
//
// SECURITY NOTE: this panel is deliberately uniform for ALL non-available
// states: revoked, unknown, notVerified, tampered. It must never confirm
// whether a vehicle exists — that would create a vehicle-existence oracle.
// The copy covers all cases without leaking state.
// ---------------------------------------------------------------------------

export function NotAvailablePanel() {
  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderLeft: `3px solid ${T.textMuted}`,
        borderRadius: RADIUS.md,
        background: T.card,
        padding: `${SPACE.md}px ${SPACE.md}px`,
      }}
      role="status"
      aria-label="Record not available"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACE.sm,
          marginBottom: SPACE.sm,
        }}
      >
        <ShieldOff size={20} color={T.textMuted} aria-hidden="true" style={{ flexShrink: 0 }} />
        <h1
          style={{
            fontFamily: F.display,
            fontSize: 20,
            fontWeight: 600,
            color: T.text,
            margin: 0,
          }}
        >
          We can&rsquo;t show anything for this link
        </h1>
      </div>
      <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, margin: 0 }}>
        This link didn&rsquo;t open a dossier. It may be mistyped, out of date, or no longer in use.
      </p>
      <p
        style={{
          fontSize: 12,
          color: T.textMuted,
          lineHeight: 1.5,
          marginTop: SPACE.sm,
          marginBottom: 0,
        }}
      >
        If someone sent you this link, ask them to share a current one from their Absolute Pinks
        account.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorPanel
// ---------------------------------------------------------------------------

export function ErrorPanel({ message }: { message: string }) {
  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderLeft: `3px solid ${T.red}`,
        borderRadius: RADIUS.md,
        background: T.card,
        padding: `${SPACE.md}px ${SPACE.md}px`,
      }}
      role="alert"
      aria-label="Verification error"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACE.sm,
          marginBottom: SPACE.sm,
        }}
      >
        <AlertCircle size={20} color={T.red} aria-hidden="true" style={{ flexShrink: 0 }} />
        <h1
          style={{
            fontFamily: F.display,
            fontSize: 20,
            fontWeight: 600,
            color: T.text,
            margin: 0,
          }}
        >
          Verification unavailable
        </h1>
      </div>
      <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, margin: 0 }}>{message}</p>
    </div>
  );
}
